import { useEffect, useState, type ReactNode } from "react";
import type { ApiClient } from "../api/client";

/**
 * SHARE MODE — the whole app when the bot's «دعوت دوستان» button opens it with
 * `?bing_action=share-invite`.
 *
 * It does ONE thing: mint the invite card (`POST /me/invite/prepared`) and hand
 * it to Telegram's native share sheet (`shareMessage`), then close. It renders
 * INSTEAD of the app shell, so it never waits on the boot chain (profile,
 * channel gate, games, sockets) — the old version subscribed to
 * `status === "ready"` and, when anything in that chain stalled or the SDK
 * reported `shareMessage` unavailable, it silently landed the user on the lobby:
 * the invite button looked like it did nothing.
 *
 * Every failure is now VISIBLE, with a working fallback (the invite link, which
 * carries only the opaque token — no Telegram id, no internal id).
 */
type Phase =
  { kind: "sharing" } | { kind: "done" } | { kind: "error"; link: string | null; detail: string };

export function ShareInvite({ api }: { api: ApiClient }): ReactNode {
  const [phase, setPhase] = useState<Phase>({ kind: "sharing" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let link: string | null = null;
      try {
        const prepared = await api.createInviteShare();
        link = prepared.link ?? null;
        const sdk = await import("@telegram-apps/sdk");
        // Deliberately NOT gated on `isAvailable()` — that check is conservative
        // and returning early was the silent failure. Call it and let the SDK
        // throw; the catch below surfaces the real reason to the user.
        await sdk.shareMessage(prepared.id);
        if (cancelled) return;
        setPhase({ kind: "done" });
        // The app only opened to host the sheet — get out of the way.
        try {
          sdk.miniApp.close();
        } catch {
          /* older client: leave the screen up */
        }
      } catch (error) {
        console.warn("[invite-share] failed", error);
        // Surface WHAT failed — a mystery error screen is barely better than a
        // silent one (this is how the unusable `shareMessage` was identified).
        const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        if (!cancelled) setPhase({ kind: "error", link, detail });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <div
      dir="rtl"
      className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <img
        src="/banners/banner-invite.jpg"
        alt=""
        className="w-full max-w-[320px] rounded-2xl shadow-lg"
      />
      {phase.kind === "error" ? (
        <>
          <p className="font-body-lg text-body-lg text-on-surface">
            پنجرهٔ اشتراک‌گذاری باز نشد 😕
          </p>
          <p className="font-caption text-caption text-on-surface-variant">
            نسخهٔ تلگرامت را به‌روز کن، یا لینک دعوتت را دستی بفرست:
          </p>
          <p className="font-caption text-caption text-on-surface-variant/70" dir="ltr">
            {phase.detail}
          </p>
          {phase.link ? (
            <button
              type="button"
              className="w-full max-w-[320px] rounded-xl bg-secondary-container px-4 py-3 font-body-md text-body-md text-on-secondary-container"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(phase.link!)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              {copied ? "کپی شد ✓" : "📋 کپی لینک دعوت"}
            </button>
          ) : null}
        </>
      ) : (
        <>
          <div
            aria-hidden
            className="h-10 w-10 animate-spin rounded-full border-4 border-outline-variant border-t-secondary"
          />
          <p className="font-body-lg text-body-lg text-on-surface">
            در حال باز کردن پنجرهٔ اشتراک‌گذاری…
          </p>
          <p className="font-caption text-caption text-on-surface-variant">
            دوستانت را انتخاب کن تا دعوت‌نامه برایشان برود 🎴
          </p>
        </>
      )}
    </div>
  );
}
